import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';

import { MobilePageShellComponent } from '../../../shared/mobile/mobile-page-shell/mobile-page-shell.component';
import { MobileDataCardComponent, MobileCardField } from '../../../shared/mobile/mobile-data-card/mobile-data-card.component';
import { MobileMetricRibbonComponent } from '../../../shared/mobile/mobile-metric-ribbon/mobile-metric-ribbon.component';
import { BomItemOffcanvasComponent, BomFormPayload } from './bom-item-offcanvas/bom-item-offcanvas.component';
import { FactoryOrderOffcanvasComponent, FactoryOrderFormPayload } from './factory-order-offcanvas/factory-order-offcanvas.component';
import { SingleSelectDropdownComponent, Option } from '../../business-details/single-select-dropdown.component';
import { MobileCategoryTabsComponent, CategoryTab } from '../../../shared/mobile/mobile-category-tabs/mobile-category-tabs.component';

@Component({
  selector: 'app-mobile-manufacturing-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MobilePageShellComponent,
    MobileDataCardComponent,
    MobileMetricRibbonComponent,
    BomItemOffcanvasComponent,
    FactoryOrderOffcanvasComponent,
    SingleSelectDropdownComponent,
    MobileCategoryTabsComponent,
  ],
  templateUrl: './mobile-manufacturing-view.component.html',
  styleUrls: ['./mobile-manufacturing-view.component.css'],
})
export class MobileManufacturingViewComponent implements OnInit, OnDestroy {
  // Data inputs
  @Input() boms: any[] = [];
  @Input() orders: any[] = [];
  @Input() ordersByStatus: { [key: string]: any[] } = {};
  @Input() products: any[] = [];
  @Input() productsWithBOMs: any[] = [];
  @Input() rawMaterials: any[] = [];
  @Input() uoms: any[] = [];
  @Input() statuses: string[] = ['Pending', 'In Progress', 'Quality Check', 'Completed', 'Sold', 'Partially Sold'];
  @Input() totalOrders = 0;
  @Input() totalPendingOrders = 0;
  @Input() totalCompletedTodayOrders = 0;
  @Input() userRole = '';
  @Input() showInactiveBOMs = false;
  @Input() completedFactoryOrders: any[] = [];
  @Input() showSoldOrders = false;

  // Outputs
  @Output() saveBom = new EventEmitter<BomFormPayload>();
  @Output() deleteBom = new EventEmitter<any>();
  @Output() toggleBomActive = new EventEmitter<any>();
  @Output() saveFactoryOrder = new EventEmitter<FactoryOrderFormPayload>();
  @Output() updateOrderStatus = new EventEmitter<{ order: any; newStatus: string }>();
  @Output() updateOrderProgress = new EventEmitter<{ order: any }>();
  @Output() sellOrder = new EventEmitter<{ order: any; qty: number }>();
  @Output() toggleShowSoldOrders = new EventEmitter<void>();

  activeView = 'bom';
  private sub = new Subscription();

  // BOM filter
  bomFilter = 'active';

  // BOM offcanvas
  bomOffcanvasVisible = false;
  bomOffcanvasEditing = false;
  bomOffcanvasItem: any = null;

  // Order offcanvas
  orderOffcanvasVisible = false;
  orderOffcanvasItem: any = null;
  showCompletedOrders = false;

  // Products tab
  sellQtyMap: { [key: number]: number } = {};

  // Workflow accordion (kept for reference — tabs replace accordion UI)
  expandedStatuses: Set<string> = new Set(['Pending']);

  // Workflow category tabs
  activeWorkflowStatus = 'Pending';

  // Swipe state
  swipeOrderId: any = null;
  swipeDeltaX = 0;
  private swipeStartX = 0;
  private swipeStartY = 0;
  private swipeActive = false;
  private readonly SWIPE_THRESHOLD = 80;

  // Status picker (opened via explicit per-card button)
  statusPickerOrder: any = null;
  statusPickerVisible = false;
  statusPickerIsClosing = false;
  statusPickerSwipeDeltaY = 0;
  statusPickerSwipeActive = false;
  private statusPickerSwipeStartY = 0;
  private statusPickerSwipeSheetHeight = 0;

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.sub.add(
      this.route.queryParams.subscribe(params => {
        this.activeView = params['view'] ?? 'bom';
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // --- BOM view ---

  get filteredBoms(): any[] {
    if (this.bomFilter === 'active') return this.boms.filter(b => b.isActive);
    if (this.bomFilter === 'inactive') return this.boms.filter(b => !b.isActive);
    return this.boms;
  }

  get activeBomCount(): number { return this.boms.filter(b => b.isActive).length; }
  get inactiveBomCount(): number { return this.boms.filter(b => !b.isActive).length; }

  get bomRibbonMetrics() {
    return [
      { label: 'Total BOMs', value: this.boms.length },
      { label: 'Active', value: this.activeBomCount },
      { label: 'Inactive', value: this.inactiveBomCount },
    ];
  }


  bomCardFields(bom: any): MobileCardField[] {
    return [
      { label: 'Status', value: bom.isActive ? 'Active' : 'Inactive', chip: true, type: 'status' },
    ];
  }

  rawMaterialName(rawMaterialItemID: number): string {
    return this.rawMaterials.find((rm: any) => rm.itemID === rawMaterialItemID)?.itemName ?? 'Unknown';
  }

  componentUomCode(component: { componentUOMID?: number | null; rawMaterialItemID?: number | null }): string {
    const selectedUomId = component?.componentUOMID;
    if (selectedUomId) {
      const found = this.uoms.find((u: any) => u.uomid === selectedUomId);
      if (found) return found.uomCode || found.uomName || '';
    }
    const material = this.rawMaterials.find((rm: any) => rm.itemID === component?.rawMaterialItemID);
    if (!material) return '';
    const uomId = material.baseUOMID ?? material.purchaseUOMID;
    if (uomId == null) return '';
    const uom = this.uoms.find((u: any) => u.uomid === uomId);
    return uom?.uomCode || uom?.uomName || '';
  }

  openAddBom(): void {
    this.bomOffcanvasItem = null;
    this.bomOffcanvasEditing = false;
    this.bomOffcanvasVisible = true;
  }

  openEditBom(bom: any): void {
    this.bomOffcanvasItem = bom;
    this.bomOffcanvasEditing = true;
    this.bomOffcanvasVisible = true;
  }

  onBomOffcanvasClose(): void {
    this.bomOffcanvasVisible = false;
    this.bomOffcanvasItem = null;
  }

  onBomSave(payload: BomFormPayload): void {
    this.saveBom.emit(payload);
  }

  onBomFilterChange(key: string): void {
    this.bomFilter = key;
    this.bomsCurrentPage = 1;
  }

  // --- Orders view ---

  trackByOrderId(_index: number, order: any): any {
    return order.productionOrderID ?? order.orderNumber ?? _index;
  }

  get filteredOrders(): any[] {
    return this.orders.filter(o => {
      if (o.status === 'Sold' || o.status === 'Partially Sold') return false;
      if (!this.showCompletedOrders && o.status === 'Completed') return false;
      return true;
    });
  }

  get orderRibbonMetrics() {
    return [
      { label: 'Total', value: this.totalOrders },
      { label: 'Pending', value: this.totalPendingOrders },
      { label: 'Done Today', value: this.totalCompletedTodayOrders },
    ];
  }

  orderStatusTone(status: string): 'primary' | 'success' | 'danger' | undefined {
    switch (status) {
      case 'Pending': return 'danger';
      case 'In Progress': return 'primary';
      case 'Quality Check': return 'primary';
      case 'Completed': return 'success';
      case 'Sold': return 'success';
      case 'Partially Sold': return 'danger';
      default: return undefined;
    }
  }

  orderCardFields(order: any): MobileCardField[] {
    return [
      { label: 'Qty', value: order.quantityToProduce, chip: true },
      { label: 'Due', value: order.plannedEndDate ? new Date(order.plannedEndDate).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' }) : '—', chip: true },
      { label: 'Status', value: order.status, chip: true, tone: this.orderStatusTone(order.status) },
    ];
  }

  getProductName(itemId: number): string {
    const p = this.products.find(pr => pr.itemID === itemId);
    return p?.itemName ?? `Order #${itemId}`;
  }

  getProductCode(itemId: number): string {
    const p = this.products.find(pr => pr.itemID === itemId);
    return p?.itemCode ?? '';
  }

  openAddOrder(): void {
    this.orderOffcanvasItem = null;
    this.orderOffcanvasVisible = true;
  }

  onOrderOffcanvasClose(): void {
    this.orderOffcanvasVisible = false;
    this.orderOffcanvasItem = null;
  }

  onOrderSave(payload: FactoryOrderFormPayload): void {
    this.saveFactoryOrder.emit(payload);
  }

  onInlineStatusChange(order: any, newStatus: string): void {
    if (order.status !== newStatus) {
      this.updateOrderStatus.emit({ order, newStatus });
    }
  }

  // Memoize so [options]="statusOptions(order)" returns a STABLE array reference
  // across change-detection cycles. A fresh array each cycle (combined with the
  // dropdown's *ngFor) tore down/rebuilt every option node on each CD while the
  // panel was open — freezing touch on iOS. The resolved status set only varies
  // by whether the order is Pending and by the source `statuses` list, so we key
  // the cache on both. (Defense-in-depth alongside the dropdown's trackBy.)
  private statusOptionsCache = new Map<string, Option[]>();
  private statusOptionsCacheStatuses: string[] | null = null;

  statusOptions(order: any): Option[] {
    if (this.statusOptionsCacheStatuses !== this.statuses) {
      this.statusOptionsCache.clear();
      this.statusOptionsCacheStatuses = this.statuses;
    }
    const key = order?.status === 'Pending' ? 'pending' : 'other';
    let cached = this.statusOptionsCache.get(key);
    if (!cached) {
      cached = this.workflowStatuses(order).map(s => ({ value: s, label: s }));
      this.statusOptionsCache.set(key, cached);
    }
    return cached;
  }

  onStatusSelectChange(order: any, value: string | null): void {
    if (value && order.status !== value) {
      this.updateOrderStatus.emit({ order, newStatus: value });
    }
  }

  workflowStatuses(order?: any): string[] {
    return this.statuses.filter(s => {
      if (s === 'Partially Sold' || s === 'Sold') return false;
      if (!order) return true;
      if (s === 'Pending') return order.status === 'Pending';
      return true;
    });
  }

  get workflowAccordionStatuses(): string[] {
    return this.statuses.filter(s => s !== 'Partially Sold' && s !== 'Sold');
  }

  private readonly STATUS_SEQUENCE = ['Pending', 'In Progress', 'Quality Check', 'Completed'];

  nextStatus(order: any): string | null {
    const idx = this.STATUS_SEQUENCE.indexOf(order.status);
    return idx >= 0 && idx < this.STATUS_SEQUENCE.length - 1
      ? this.STATUS_SEQUENCE[idx + 1]
      : null;
  }

  advanceOrder(order: any): void {
    const next = this.nextStatus(order);
    if (next) {
      this.updateOrderStatus.emit({ order, newStatus: next });
    }
  }

  // --- Workflow view ---

  get workflowRibbonMetrics() {
    return this.statuses
      .filter(s => s !== 'Partially Sold')
      .map(s => ({ label: s, value: (this.ordersByStatus[s] ?? []).length }));
  }

  toggleStatus(status: string): void {
    if (this.expandedStatuses.has(status)) {
      this.expandedStatuses.delete(status);
    } else {
      this.expandedStatuses.add(status);
    }
  }

  isStatusExpanded(status: string): boolean {
    return this.expandedStatuses.has(status);
  }

  getStatusOrders(status: string): any[] {
    return this.ordersByStatus[status] ?? [];
  }

  // Open status picker from an explicit per-card button (reliable on iOS).
  openStatusPicker(order: any): void {
    this.statusPickerOrder = order;
    this.statusPickerVisible = true;
  }

  onStatusBtnTouch(order: any, event: TouchEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.openStatusPicker(order);
  }

  pickStatusTouch(status: string, event: TouchEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.pickStatus(status);
  }

  pickStatus(newStatus: string): void {
    if (this.statusPickerOrder) {
      this.updateOrderStatus.emit({ order: this.statusPickerOrder, newStatus });
    }
    this.closeStatusPicker();
  }

  closeStatusPicker(): void {
    this.statusPickerIsClosing = true;
    setTimeout(() => {
      this.statusPickerVisible = false;
      this.statusPickerIsClosing = false;
      this.statusPickerSwipeDeltaY = 0;
      this.statusPickerSwipeActive = false;
      this.statusPickerOrder = null;
    }, 280);
  }

  onStatusPickerTouchStart(e: TouchEvent): void {
    const sheet = (e.currentTarget as HTMLElement).closest('.m-del-sheet') as HTMLElement;
    this.statusPickerSwipeSheetHeight = sheet?.offsetHeight ?? 300;
    this.statusPickerSwipeStartY = e.touches[0].clientY;
    this.statusPickerSwipeDeltaY = 0;
    this.statusPickerSwipeActive = true;
  }

  onStatusPickerTouchMove(e: TouchEvent): void {
    if (!this.statusPickerSwipeActive) return;
    this.statusPickerSwipeDeltaY = Math.max(0, e.touches[0].clientY - this.statusPickerSwipeStartY);
  }

  onStatusPickerTouchEnd(): void {
    if (!this.statusPickerSwipeActive) return;
    this.statusPickerSwipeActive = false;
    if (this.statusPickerSwipeDeltaY > this.statusPickerSwipeSheetHeight * 0.3) {
      this.closeStatusPicker();
    } else {
      this.statusPickerSwipeDeltaY = 0;
    }
  }

  get statusPickerSwipeProgress(): number {
    if (!this.statusPickerSwipeSheetHeight) return 0;
    return Math.min(1, this.statusPickerSwipeDeltaY / this.statusPickerSwipeSheetHeight);
  }

  sanitizeStatus(status: string): string {
    return status.toLowerCase().replace(/\s+/g, '-');
  }

  // --- Workflow category tabs ---

  get workflowCategoryTabs(): CategoryTab[] {
    const iconMap: { [k: string]: string } = {
      'Pending': 'bi-hourglass-split',
      'In Progress': 'bi-gear',
      'Quality Check': 'bi-clipboard-check',
      'Completed': 'bi-check-circle',
    };
    return this.workflowAccordionStatuses.map(s => ({
      key: s,
      label: s,
      icon: iconMap[s] ?? 'bi-circle',
      count: this.getStatusOrders(s).length,
    }));
  }

  selectWorkflowStatus(key: string): void {
    this.activeWorkflowStatus = key;
    this.workflowCurrentPage = 1;
  }

  get activeWorkflowStatusIcon(): string {
    const iconMap: { [k: string]: string } = {
      'Pending': 'bi-hourglass-split',
      'In Progress': 'bi-gear',
      'Quality Check': 'bi-clipboard-check',
      'Completed': 'bi-check-circle',
    };
    return iconMap[this.activeWorkflowStatus] ?? 'bi-circle';
  }

  previousStatus(order: any): string | null {
    const idx = this.STATUS_SEQUENCE.indexOf(order.status);
    return idx > 0 ? this.STATUS_SEQUENCE[idx - 1] : null;
  }

  // --- Progress bar ---

  onProgressChange(event: Event, order: any): void {
    order.completionPercentage = parseInt((event.target as HTMLInputElement).value, 10);
    this.updateOrderProgress.emit({ order });
  }

  // --- Swipe ---

  onCardSwipeStart(order: any, event: TouchEvent): void {
    this.swipeOrderId = order.productionOrderID;
    this.swipeStartX = event.touches[0].clientX;
    this.swipeStartY = event.touches[0].clientY;
    this.swipeDeltaX = 0;
    this.swipeActive = true;
  }

  onCardSwipeMove(order: any, event: TouchEvent): void {
    if (!this.swipeActive || this.swipeOrderId !== order.productionOrderID) return;
    const currentX = event.touches[0].clientX;
    const deltaX = currentX - this.swipeStartX;
    const deltaY = event.touches[0].clientY - this.swipeStartY;
    // Only claim the gesture once it's clearly horizontal — keeps native vertical
    // scroll working and stops iOS from fighting the swipe mid-drag.
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      event.preventDefault();
      this.swipeDeltaX = deltaX;
    }
  }

  onCardSwipeEnd(order: any): void {
    if (!this.swipeActive || this.swipeOrderId !== order.productionOrderID) return;
    this.swipeActive = false;
    const delta = this.swipeDeltaX;
    this.swipeDeltaX = 0;
    this.swipeOrderId = null;
    if (delta > this.SWIPE_THRESHOLD) {
      this.advanceOrder(order);
    } else if (delta < -this.SWIPE_THRESHOLD) {
      const prev = this.previousStatus(order);
      if (prev) {
        this.updateOrderStatus.emit({ order, newStatus: prev });
      }
    }
  }

  swipeTranslate(order: any): string {
    if (this.swipeOrderId !== order.productionOrderID) return 'translateX(0)';
    return `translateX(${this.swipeDeltaX}px)`;
  }

  swipeHintSide(order: any): 'next' | 'prev' | null {
    if (this.swipeOrderId !== order.productionOrderID) return null;
    if (this.swipeDeltaX > 10) return 'next';
    if (this.swipeDeltaX < -10) return 'prev';
    return null;
  }

  toggleShowCompletedOrders(): void {
    this.showCompletedOrders = !this.showCompletedOrders;
    this.ordersCurrentPage = 1;
  }

  // ── Pagination ─────────────────────────────────────────────────────────────
  readonly pageSize = 10;
  bomsCurrentPage = 1;
  ordersCurrentPage = 1;
  workflowCurrentPage = 1;
  productsCurrentPage = 1;

  get filteredBomsTotalPages(): number { return Math.max(1, Math.ceil(this.filteredBoms.length / this.pageSize)); }
  get filteredBomsPaged(): any[] { const s = (this.bomsCurrentPage - 1) * this.pageSize; return this.filteredBoms.slice(s, s + this.pageSize); }
  changeBomsPage(page: number): void { if (page >= 1 && page <= this.filteredBomsTotalPages) this.bomsCurrentPage = page; }

  get filteredOrdersTotalPages(): number { return Math.max(1, Math.ceil(this.filteredOrders.length / this.pageSize)); }
  get filteredOrdersPaged(): any[] { const s = (this.ordersCurrentPage - 1) * this.pageSize; return this.filteredOrders.slice(s, s + this.pageSize); }
  changeOrdersPage(page: number): void { if (page >= 1 && page <= this.filteredOrdersTotalPages) this.ordersCurrentPage = page; }

  get workflowOrdersTotalPages(): number { return Math.max(1, Math.ceil(this.getStatusOrders(this.activeWorkflowStatus).length / this.pageSize)); }
  get workflowOrdersPaged(): any[] { const s = (this.workflowCurrentPage - 1) * this.pageSize; return this.getStatusOrders(this.activeWorkflowStatus).slice(s, s + this.pageSize); }
  changeWorkflowPage(page: number): void { if (page >= 1 && page <= this.workflowOrdersTotalPages) this.workflowCurrentPage = page; }

  get completedFactoryOrdersTotalPages(): number { return Math.max(1, Math.ceil(this.completedFactoryOrders.length / this.pageSize)); }
  get completedFactoryOrdersPaged(): any[] { const s = (this.productsCurrentPage - 1) * this.pageSize; return this.completedFactoryOrders.slice(s, s + this.pageSize); }
  changeProductsPage(page: number): void { if (page >= 1 && page <= this.completedFactoryOrdersTotalPages) this.productsCurrentPage = page; }
}
